import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.categories'), to: '/#categories' },
    { label: t('nav.shoppingGuide'), to: '/usfans-spreadsheet' },
    { label: t('nav.safety'), to: '/#safety' },
  ];

  const navLinkClass = transparent
    ? 'text-background-50 hover:text-primary-300'
    : 'text-foreground-800 hover:text-primary-600';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        transparent
          ? 'bg-gradient-to-b from-black/45 via-black/20 to-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200 shadow-none'
      }`}
    >
      <nav className="mx-auto flex h-16 items-center justify-between px-4 md:px-6 lg:px-10">
        <Link
          to="/"
          className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight whitespace-nowrap cursor-pointer"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-2 text-base leading-none"></i>
          </span>
          <span className={transparent ? 'text-background-50' : 'text-foreground-950'}>
            USFans <span className="text-primary-500">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to + l.label}
              to={l.to}
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${navLinkClass}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher tone={transparent ? 'dark' : 'light'} />
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            {t('nav.buyNow')}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden flex h-9 w-9 items-center justify-center rounded-lg cursor-pointer"
          aria-label="Menu"
        >
          <i
            className={`text-xl leading-none ${
              transparent ? 'text-background-50' : 'text-foreground-950'
            } ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}
          ></i>
        </button>
      </nav>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to + l.label}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                {l.label}
              </Link>
            ))}

            <div className="mt-2 border-t border-background-200 pt-2">
              <button
                type="button"
                onClick={() => setMobileCategoriesOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                <span>{t('nav.categories')}</span>
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i
                    className={`text-base leading-none ${
                      mobileCategoriesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                    }`}
                  ></i>
                </span>
              </button>

              {mobileCategoriesOpen && (
                <div className="mt-1 grid grid-cols-2 gap-1.5 px-1">
                  {categories.map((c) => (
                    <a
                      key={c.id}
                      href={c.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      <span className="flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-arrow-right-up-line text-xs leading-none"></i>
                      </span>
                      <span className="truncate">{t(`categories.${c.id}`)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
              <LanguageSwitcher tone="light" />
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                {t('nav.buyNow')}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}